// src/components/Navbars/AdminNavbar.js
import React, { useContext, useState, useEffect } from "react";
import classnames from "classnames";
import { useLocation } from "react-router-dom";
import {
  Navbar,
  NavbarBrand,
  Nav,
  NavItem,
  NavLink,
  Collapse,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Container
} from "reactstrap";
import { DataContext } from "../../context/DataContext";
import routes from "routes.js";
import AddNewOrderModal from "../../views/AddNewOrderModal";
import { updateAlert } from "../../context/api";
import moment from "moment";
import { syncOrderIntoCustomers } from "../../utils/orderHistory";

function AdminNavbar() {
  const { data, setData } = useContext(DataContext);
  const notifications         = (data.alerts || []).slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const isSuperuser           = data.userProfile?.is_superuser;
  const location              = useLocation();

  const [collapseOpen, setCollapseOpen]           = useState(false);
  const [sidebarOpen, setSidebarOpen]             = useState(false);
  const [color, setColor]                         = useState("navbar-transparent");
  const [addOrderModalOpen, setAddOrderModalOpen] = useState(false);

  // any unread?
  const hasUnread = notifications.some(n => !n.isRead);

  // sidebar toggle (mobile)
  const toggleSidebar = () => {
    document.documentElement.classList.toggle("nav-open");
    setSidebarOpen(v => !v);
  };

  // collapse toggle (mobile)
  const toggleCollapse = () => {
    setColor(c => c === "navbar-transparent" ? "bg-white" : "navbar-transparent");
    setCollapseOpen(v => !v);
  };

  // keep nav color responsive
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 993 && collapseOpen) setColor("bg-white");
      else setColor("navbar-transparent");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [collapseOpen]);

  // derive brand text
  const getBrandText = () => {
    for (let r of routes) {
      if (location.pathname.includes(r.layout + r.path)) return r.header;
    }
    return "Customer Relationship Management";
  };

  // mark a notification as read
  const markAsRead = async (alertId) => {
    try {
      await updateAlert(alertId, { isRead: true });
      setData(d => ({
        ...d,
        alerts: d.alerts.map(a =>
          a.id === alertId ? { ...a, isRead: true } : a
        )
      }));
    } catch (err) {
      console.error("Failed to mark read:", err);
    }
  };

  // Add Order modal helpers
  const openAddOrderModal   = () => setAddOrderModalOpen(true);
  const toggleAddOrderModal = () => setAddOrderModalOpen(v => !v);

  const handleNavbarAddOrder = (newOrder) => {
    const updatedCustomers = syncOrderIntoCustomers(data.customers || [], newOrder);
    setData({ ...data, customers: updatedCustomers });
    setAddOrderModalOpen(false);
  };

  return (
    <>
      <Navbar className={classnames("navbar-absolute fixed-top", color)} expand="lg">
        <Container fluid>
          <div className="navbar-wrapper">
            <div className={classnames("navbar-toggle", { toggled: sidebarOpen })}>
              <button className="navbar-toggler" onClick={toggleSidebar}>
                <span className="navbar-toggler-bar bar1" />
                <span className="navbar-toggler-bar bar2" />
                <span className="navbar-toggler-bar bar3" />
              </button>
            </div>
            <NavbarBrand href="#!" onClick={e => e.preventDefault()}>
              <span className="d-none d-md-block">{getBrandText()}</span>
              <span className="d-block d-md-none">CRM</span>
            </NavbarBrand>
          </div>

          <button
            className="navbar-toggler"
            onClick={toggleCollapse}
            aria-controls="navigation-index"
            aria-expanded={collapseOpen}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-bar navbar-kebab" />
            <span className="navbar-toggler-bar navbar-kebab" />
            <span className="navbar-toggler-bar navbar-kebab" />
          </button>

          <Collapse navbar isOpen={collapseOpen} className="justify-content-end">
            <Nav navbar>
              {isSuperuser && (
                <NavItem>
                  <NavLink href="/admin/dashboard" className="d-flex flex-column align-items-center">
                    <i className="nc-icon nc-bank" style={{ fontSize: "1.5rem" }} />
                    <p className="mt-1" style={{ textTransform: "none" }}>My Dashboard</p>
                  </NavLink>
                </NavItem>
              )}

              <NavItem>
                <NavLink href="#" onClick={e => { e.preventDefault(); openAddOrderModal(); }}
                         className="d-flex flex-column align-items-center">
                  <i className="nc-icon nc-money-coins" style={{ fontSize: "1.5rem" }} />
                  <p className="mt-1" style={{ textTransform: "none" }}>Add Order</p>
                </NavLink>
              </NavItem>

              <UncontrolledDropdown nav inNavbar>
                <DropdownToggle nav className="d-flex flex-column align-items-center">
                  <i
                    className="nc-icon nc-bell-55"
                    style={{ fontSize: "1.5rem", color: hasUnread ? "red" : undefined }}
                  />
                  <p className="mt-1" style={{ textTransform: "none", color: hasUnread ? "red" : undefined }}>
                    Alerts
                  </p>
                </DropdownToggle>

                <DropdownMenu right style={{ minWidth: "300px" }}>
                  <DropdownItem header>Alerts</DropdownItem>
                  {notifications.length === 0 && (
                    <DropdownItem disabled>No alerts</DropdownItem>
                  )}
                  {notifications.map((notif, i) => (
                    <React.Fragment key={notif.id}>
                      <DropdownItem
                        onClick={() => markAsRead(notif.id)}
                        style={{
                          backgroundColor: notif.isRead ? undefined : "#f8d7da",
                          cursor: "pointer"
                        }}
                      >
                        <div className="d-flex align-items-start">
                          <span className="mr-2" style={{ fontSize: "1.25rem" }}>
                            <i
                              className={
                                notif.alertType === "everyone"
                                  ? "fa fa-users"
                                  : "fa fa-user-circle"
                              }
                            />
                          </span>
                          <div>
                            <strong>
                              {notif.alertType === "specific"
                                ? "Direct alert"
                                : "Company alert"}
                            </strong>
                            <small className="text-muted ml-1">{moment(notif.createdAt).fromNow()}</small>
                            <br /> {/* Explicit line break */}
                            <p className="mb-0">{notif.message}</p>
                          </div>
                        </div>
                      </DropdownItem>
                      {i < notifications.length - 1 && <DropdownItem divider />}
                    </React.Fragment>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>

            </Nav>
          </Collapse>
        </Container>
      </Navbar>

      <AddNewOrderModal
        isOpen={addOrderModalOpen}
        toggle={toggleAddOrderModal}
        onSave={handleNavbarAddOrder}
      />
    </>
  );
}

export default AdminNavbar;
